import Phaser from 'phaser';
import { World } from '../ecs/World';
import { BeastieComponent, ElementType } from '../ecs/components/BeastieComponent';
import { HealthComponent, StatusEffect } from '../ecs/components/HealthComponent';
import { EventBus } from '../utils/EventBus';

interface PartyMemberUI {
  entityId: string;
  container: Phaser.GameObjects.Container;
  nameText: Phaser.GameObjects.Text;
  levelText: Phaser.GameObjects.Text;
  hpBar: Phaser.GameObjects.Graphics;
  hpText: Phaser.GameObjects.Text;
  energyBar: Phaser.GameObjects.Graphics;
  energyText: Phaser.GameObjects.Text;
  elementIcon: Phaser.GameObjects.Graphics;
  xpBar: Phaser.GameObjects.Graphics;
  statusContainer: Phaser.GameObjects.Container;
  faintedOverlay: Phaser.GameObjects.Graphics;
  faintedText: Phaser.GameObjects.Text;
}

/**
 * PartyStatusPanel - Left sidebar showing party member status
 * Displays name, level, HP/energy bars, element, status effects, XP progress
 */
export class PartyStatusPanel {
  private scene: Phaser.Scene;
  private world: World;
  private container!: Phaser.GameObjects.Container;
  private memberUIs: Map<string, PartyMemberUI> = new Map();

  // Panel dimensions
  private panelWidth = 180;
  private memberHeight = 85;
  private padding = 6;
  private xPosition = 10;
  private yPosition = 90; // Below debug text

  // Element colors
  private elementColors: Record<ElementType, number> = {
    solar: 0xffaa00,
    verdant: 0x44cc44,
    aqua: 0x4488ff,
    aero: 0xccccff,
    terra: 0x886644,
    void: 0x6622aa,
    tech: 0x888888,
    spirit: 0xff88ff,
  };

  // Status effect colors
  private statusColors: Record<string, number> = {
    poison: 0x88ff00,
    burn: 0xff4400,
    freeze: 0x00ccff,
    paralysis: 0xffff00,
    sleep: 0x8888ff,
    confusion: 0xff88ff,
    regen: 0x00ff88,
    shield: 0xaaaaaa,
    haste: 0xffcc00,
    strength: 0xff8800,
    weakness: 0x884400,
  };

  constructor(scene: Phaser.Scene, world: World) {
    this.scene = scene;
    this.world = world;
    this.createPanel();
    this.setupEventListeners();
  }

  private createPanel(): void {
    this.container = this.scene.add.container(this.xPosition, this.yPosition);
    this.container.setScrollFactor(0);
    this.container.setDepth(1000);

    // Initial build
    this.refreshPartyMembers();
  }

  private setupEventListeners(): void {
    EventBus.onEvent('beastie:healed', () => this.updateAllBars());
    EventBus.onEvent('beastie:levelUp', () => this.refreshPartyMembers());
    EventBus.onEvent('combat:attack', () => this.updateAllBars());
    EventBus.onEvent('taming:captured', () => this.refreshPartyMembers());
    EventBus.onEvent('beastie:revived', () => this.updateAllBars());
  }

  /**
   * Update called each frame
   */
  public update(): void {
    this.updateAllBars();
  }

  /**
   * Refresh party member list (call when members change)
   */
  public refreshPartyMembers(): void {
    // Clear existing UI
    for (const ui of this.memberUIs.values()) {
      ui.container.destroy(true);
    }
    this.memberUIs.clear();

    // Get party members (entities with 'party_member' tag that have beastie component)
    const partyMembers = this.world.getEntitiesByTag('party_member');

    let yOffset = 0;
    for (const entity of partyMembers) {
      const beastie = entity.getComponent<BeastieComponent>('beastie');
      const health = entity.getComponent<HealthComponent>('health');

      // Only show beasties, not the player
      if (beastie && health) {
        const memberUI = this.createMemberUI(entity.id, beastie, health, yOffset);
        this.memberUIs.set(entity.id, memberUI);
        yOffset += this.memberHeight + this.padding;
      }
    }
  }

  private createMemberUI(
    entityId: string,
    beastie: BeastieComponent,
    _health: HealthComponent,
    yOffset: number
  ): PartyMemberUI {
    const memberContainer = this.scene.add.container(0, yOffset);

    // Background panel
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x000000, 0.75);
    bg.fillRoundedRect(0, 0, this.panelWidth, this.memberHeight, 6);
    bg.lineStyle(1, 0x444444, 0.8);
    bg.strokeRoundedRect(0, 0, this.panelWidth, this.memberHeight, 6);
    memberContainer.add(bg);

    // Element icon (left side)
    const elementIcon = this.createElementIcon(beastie.elementTypes[0] || 'void');
    elementIcon.setPosition(14, 14);
    memberContainer.add(elementIcon);

    // Name text
    const nameText = this.scene.add.text(30, 5, beastie.nickname || beastie.speciesId, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    memberContainer.add(nameText);

    // Level text (right-aligned)
    const levelText = this.scene.add
      .text(this.panelWidth - 8, 5, `Lv.${beastie.level}`, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#aaaaaa',
      })
      .setOrigin(1, 0);
    memberContainer.add(levelText);

    // HP bar
    const hpBar = this.scene.add.graphics();
    hpBar.setPosition(8, 24);
    memberContainer.add(hpBar);

    const hpText = this.scene.add
      .text(this.panelWidth - 8, 24, '', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(1, 0);
    memberContainer.add(hpText);

    // Energy bar
    const energyBar = this.scene.add.graphics();
    energyBar.setPosition(8, 38);
    memberContainer.add(energyBar);

    const energyText = this.scene.add
      .text(this.panelWidth - 8, 38, '', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(1, 0);
    memberContainer.add(energyText);

    // XP bar (thin, at bottom)
    const xpBar = this.scene.add.graphics();
    xpBar.setPosition(8, 54);
    memberContainer.add(xpBar);

    // XP label
    const xpLabel = this.scene.add.text(8, 54, 'XP', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#888888',
    });
    memberContainer.add(xpLabel);

    // Status effects container
    const statusContainer = this.scene.add.container(8, 68);
    memberContainer.add(statusContainer);

    // Fainted overlay (initially hidden)
    const faintedOverlay = this.scene.add.graphics();
    faintedOverlay.fillStyle(0x000000, 0.6);
    faintedOverlay.fillRoundedRect(0, 0, this.panelWidth, this.memberHeight, 6);
    faintedOverlay.setVisible(false);
    memberContainer.add(faintedOverlay);

    // Fainted text (initially hidden)
    const faintedText = this.scene.add
      .text(this.panelWidth / 2, this.memberHeight / 2, 'FAINTED', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ff6666',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    faintedText.setVisible(false);
    memberContainer.add(faintedText);

    this.container.add(memberContainer);

    const memberUI: PartyMemberUI = {
      entityId,
      container: memberContainer,
      nameText,
      levelText,
      hpBar,
      hpText,
      energyBar,
      energyText,
      elementIcon,
      xpBar,
      statusContainer,
      faintedOverlay,
      faintedText,
    };

    // Initial draw
    this.updateMemberBars(memberUI);

    return memberUI;
  }

  private createElementIcon(element: ElementType): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const color = this.elementColors[element] || 0xffffff;
    g.fillStyle(color, 1);
    g.fillCircle(0, 0, 7);
    g.lineStyle(1, 0xffffff, 0.5);
    g.strokeCircle(0, 0, 7);
    return g;
  }

  private updateAllBars(): void {
    for (const ui of this.memberUIs.values()) {
      this.updateMemberBars(ui);
    }
  }

  private updateMemberBars(ui: PartyMemberUI): void {
    const entity = this.world.getEntity(ui.entityId);
    if (!entity) return;

    const beastie = entity.getComponent<BeastieComponent>('beastie');
    const health = entity.getComponent<HealthComponent>('health');
    if (!beastie || !health) return;

    // Handle fainted state
    const isFainted = health.isFainted;
    ui.faintedOverlay.setVisible(isFainted);
    ui.faintedText.setVisible(isFainted);

    const barWidth = this.panelWidth - 55;
    const barHeight = 10;

    // HP bar
    ui.hpBar.clear();
    ui.hpBar.fillStyle(0x333333, 1);
    ui.hpBar.fillRoundedRect(0, 0, barWidth, barHeight, 2);

    // Gray color for fainted, normal colors otherwise
    const hpColor = isFainted
      ? 0x666666
      : health.healthPercent > 0.5
        ? 0x44cc44
        : health.healthPercent > 0.25
          ? 0xccaa00
          : 0xcc4444;
    ui.hpBar.fillStyle(hpColor, 1);
    ui.hpBar.fillRoundedRect(1, 1, (barWidth - 2) * health.healthPercent, barHeight - 2, 2);

    ui.hpText.setText(isFainted ? '0' : `${health.current}/${health.max}`);

    // Energy bar
    ui.energyBar.clear();
    ui.energyBar.fillStyle(0x333333, 1);
    ui.energyBar.fillRoundedRect(0, 0, barWidth, barHeight, 2);
    ui.energyBar.fillStyle(0x4488ff, 1);
    ui.energyBar.fillRoundedRect(1, 1, (barWidth - 2) * health.energyPercent, barHeight - 2, 2);
    ui.energyText.setText(`${health.energy}/${health.maxEnergy}`);

    // XP bar
    const xpPercent =
      beastie.experienceToNextLevel > 0
        ? beastie.experience / beastie.experienceToNextLevel
        : 0;
    const xpBarWidth = this.panelWidth - 30;

    ui.xpBar.clear();
    ui.xpBar.fillStyle(0x222222, 1);
    ui.xpBar.fillRect(16, 0, xpBarWidth, 6);
    ui.xpBar.fillStyle(0xaa88ff, 1);
    ui.xpBar.fillRect(16, 0, xpBarWidth * xpPercent, 6);

    // Update level text
    ui.levelText.setText(`Lv.${beastie.level}`);

    // Update status effects
    this.updateStatusEffects(ui, health.statusEffects);
  }

  private updateStatusEffects(ui: PartyMemberUI, effects: StatusEffect[]): void {
    ui.statusContainer.removeAll(true);

    let xPos = 0;
    for (const effect of effects.slice(0, 6)) {
      // Max 6 icons
      const icon = this.createStatusIcon(effect);
      icon.setPosition(xPos, 0);
      ui.statusContainer.add(icon);
      xPos += 14;
    }
  }

  private createStatusIcon(effect: StatusEffect): Phaser.GameObjects.Graphics {
    const g = this.scene.add.graphics();
    const color = this.statusColors[effect.type] || 0xffffff;
    g.fillStyle(color, 1);
    g.fillCircle(0, 0, 5);
    return g;
  }

  public destroy(): void {
    this.container.destroy(true);
    this.memberUIs.clear();
  }
}
